import { useEffect, useMemo, useCallback, useRef, FC } from 'react';
import { css } from '@emotion/react';
import { CustomKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import dayjs from 'dayjs';
import isEqual from 'lodash-es/isEqual';
import { useGetAirSettingsQuery, useUpdateCustomKnowledgeAirSettingsMutation } from '@frontend/api-ai-settings';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { FloatingSaveBar } from '@frontend/settings-ui';
import { sentry } from '@frontend/tracking';
import { useUnsavedChanges } from '@frontend/unsaved-changes-modal';
import { useModalControl, InlineNotification, useAlert } from '@frontend/design-system';
import {
  useAIRCustomKnowledgeStore,
  AIRCustomKnowledgeProvider,
  selectStoreIsDirty,
} from '../../../../stores/air-settings.store';
import { AiReceptionistTrackingIds } from '../../../../tracking-ids';
import { LastUpdatedInfo } from '../../common/last-updated-info';
import { SettingsUnsavedChangesModal } from '../../common/settings-unsaved-changes-modal';
import { MultiLocationCustomKnowledge } from './multi-location-custom-knowledge';
import { SingleLocationCustomKnowledge } from './single-location-custom-knowledge';

const createInitialSettings = (locationIds: string[]): Record<string, CustomKnowledge> => {
  return locationIds.reduce((acc, id) => {
    acc[id] = {
      records: [],
    };
    return acc;
  }, {} as Record<string, CustomKnowledge>);
};

const CustomKnowledgePageContent = () => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { selectedLocationIds } = useAppScopeStore();
  const alerts = useAlert();

  const { initialSettings, draftSettings, setInitialSettings, setDraftSettings, saveDraft, clearDraft, resetStore } =
    useAIRCustomKnowledgeStore([
      'initialSettings',
      'draftSettings',
      'setInitialSettings',
      'setDraftSettings',
      'saveDraft',
      'clearDraft',
      'resetStore',
    ]);

  const shouldProceedAfterSaveRef = useRef(false);

  const { data: airSettingsResponse, isLoading: isCustomSettingsLoading } = useGetAirSettingsQuery({
    request: {
      locationIds: selectedLocationIds,
    },
    options: {
      enabled: selectedLocationIds.length > 0,
    },
  });

  const { mutate: updateCustomKnowledgeAirSettings, isLoading: isUpdatingCustomKnowledge } =
    useUpdateCustomKnowledgeAirSettingsMutation({
      options: {
        onSuccess: () => {
          alerts.success(t('Custom knowledge settings saved successfully.'));
          saveDraft();
          if (shouldProceedAfterSaveRef.current) {
            shouldProceedAfterSaveRef.current = false;
            proceedWithUnsavedChanges();
          }
        },
        onError: (error) => {
          shouldProceedAfterSaveRef.current = false;
          alerts.error(t('Failed to save custom knowledge settings. Please try again.'));
          sentry.error({
            team: 'frontend',
            topic: 'ai-receptionist',
            error,
          });
        },
      },
    });

  useEffect(() => {
    const customSettings = airSettingsResponse?.locationAirSettings?.reduce((acc, curr) => {
      if (curr.locationId && curr.airSettings?.customKnowledge) {
        acc[curr.locationId] = curr.airSettings.customKnowledge[0];
      }
      return acc;
    }, {} as Record<string, CustomKnowledge>);

    if (customSettings && Object.keys(customSettings).length > 0) {
      setInitialSettings(customSettings);
      setDraftSettings(customSettings);
    } else {
      const defaultSettings = createInitialSettings(selectedLocationIds);
      setInitialSettings(defaultSettings);
      setDraftSettings(defaultSettings);
    }
  }, [airSettingsResponse, selectedLocationIds, setInitialSettings, setDraftSettings]);

  const isMultiLocation = selectedLocationIds.length > 1;

  const locationErrors = useAIRCustomKnowledgeStore((state) => {
    return selectedLocationIds.reduce((acc, id) => {
      const errors = state.fieldErrors[id];
      acc[id] = errors ? Object.values(errors).some(Boolean) : false;
      return acc;
    }, {} as Record<string, boolean>);
  });

  const formHasErrors = useAIRCustomKnowledgeStore((state) =>
    selectedLocationIds.some((id) => {
      const errors = state.fieldErrors[id];
      return errors ? Object.values(errors).some(Boolean) : false;
    })
  ) as boolean;
  const isDirty = useAIRCustomKnowledgeStore(selectStoreIsDirty);

  const { modalProps: unsavedChangesModalProps, openModal: openUnsavedChangesModal } = useModalControl();
  const { proceed: proceedWithUnsavedChanges, cancel: cancelUnsavedChanges } = useUnsavedChanges(
    isDirty,
    unsavedChangesModalProps.show,
    openUnsavedChangesModal
  );

  const handleSave = useCallback(() => {
    if (formHasErrors || isUpdatingCustomKnowledge) return;

    const deletedCustomKnowledgeIds: string[] = [];

    const customKnowledgePayload = Object.entries(draftSettings)
      .filter(([locationId, settings]) => {
        const initialSettingsForLocation = initialSettings[locationId] || {};

        const initialList = initialSettingsForLocation.records || [];
        const draftList = settings?.records || [];
        const draftRecordsById = new Map(
          draftList.filter((draftRecord) => !!draftRecord.id).map((draftRecord) => [draftRecord.id as string, true])
        );

        initialList.forEach((item) => {
          if (item.id && !draftRecordsById.has(item.id) && !item.id.startsWith('custom-')) {
            deletedCustomKnowledgeIds.push(item.id);
          }
        });

        return !isEqual(settings, initialSettingsForLocation);
      })
      .map(([locationId, settings]) => ({
        locationId,
        records: (settings?.records || []).map((item) => ({
          id: item.id?.startsWith('custom-') ? undefined : item.id,
          customKnowledgeTitle: item.customKnowledgeTitle,
          customKnowledgeContent: item.customKnowledgeContent,
        })),
      }));

    updateCustomKnowledgeAirSettings({
      customKnowledge: customKnowledgePayload,
      deletedCustomKnowledgeIds: deletedCustomKnowledgeIds.length > 0 ? deletedCustomKnowledgeIds : undefined,
    });
  }, [formHasErrors, isUpdatingCustomKnowledge, draftSettings, initialSettings, updateCustomKnowledgeAirSettings]);

  const handleProceedWithoutSaving = () => {
    clearDraft();
    unsavedChangesModalProps.onClose();
    proceedWithUnsavedChanges();
  };

  const lastUpdatedAt = useMemo(() => {
    const allRecords =
      airSettingsResponse?.locationAirSettings?.flatMap(
        (settings) => settings?.airSettings?.customKnowledge?.flatMap((ck) => ck.records || []) || []
      ) || [];

    const sortedRecords = allRecords
      .filter((record) => !!record.updatedAt)
      .sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());

    return sortedRecords[0]?.updatedAt ? dayjs(sortedRecords[0].updatedAt).toISOString() : undefined;
  }, [airSettingsResponse?.locationAirSettings]);

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, []);

  const isLoading = isCustomSettingsLoading || isUpdatingCustomKnowledge;

  return (
    <div css={styles.container}>
      <div css={{ flex: 1, paddingBottom: '80px' }}>
        {isMultiLocation ? (
          <MultiLocationCustomKnowledge
            locationIds={selectedLocationIds}
            settings={draftSettings}
            initialSettings={initialSettings}
            locationErrors={locationErrors}
            isLoading={isLoading}
          />
        ) : (
          <SingleLocationCustomKnowledge locationId={selectedLocationIds[0]} isLoading={isLoading} />
        )}
      </div>

      <FloatingSaveBar
        isDirty={isDirty}
        hasErrors={formHasErrors}
        extraContent={lastUpdatedAt ? <LastUpdatedInfo lastUpdatedAt={lastUpdatedAt} /> : null}
        errorBadge={
          <InlineNotification
            status='error'
            message={t('You must address the error(s) above before saving.')}
            size='medium'
          />
        }
        primaryAction={{
          label: t('Save'),
          onClick: handleSave,
          disabled: formHasErrors,
          trackingId: AiReceptionistTrackingIds.customKnowledgeSaveButton,
          hoverLabel: t('Click to update AI knowledge base. Changes should apply soon after being updated.'),
          isLoading,
        }}
      />

      <SettingsUnsavedChangesModal
        show={unsavedChangesModalProps.show}
        onClose={() => {
          unsavedChangesModalProps.onClose();
          cancelUnsavedChanges();
        }}
        onProceedWithoutSaving={handleProceedWithoutSaving}
        onSave={() => {
          shouldProceedAfterSaveRef.current = true;
          handleSave();
          unsavedChangesModalProps.onClose();
        }}
        isSaveDisabled={formHasErrors}
        description={
          <Trans t={t}>
            Before you leave, <strong>would you like to save and push changes</strong> to your AI Receptionist's custom
            knowledge?
          </Trans>
        }
      />
    </div>
  );
};

export const CustomKnowledgePage: FC = () => {
  return (
    <AIRCustomKnowledgeProvider>
      <CustomKnowledgePageContent />
    </AIRCustomKnowledgeProvider>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
  `,
};
